import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import axios from 'axios'
import { DRIVER_ENDPOINT, VEHICLE_ENDPOINT } from '@/constants/Endpoints'
import { VEHICLE_DONATION_FIELD_MAP } from '@/constants/ShoppingCartConstants'
import { useUserLogin } from '@/stores/userLogin'
import { LOGGER } from '@/util/logger'

const WORKFLOW_DEFAULTS = {
  VRRENEW: {
    renewalLength: null,
    renewalYears: null,
    odometerReading: '',
    grossAnnualIncome: '',
    incomeCertified: null,
    enrollmentElection: null,
    insurance: {
      companyName: '',
      policyNumber: '',
      naicNumber: '',
      effectiveDate: null,
      expirationDate: null,
      certified: null,
    },
    duplicateCount: 0,
    selectedDonations: [],
  },
  VRDUPLICATE: {
    unableToPrint: null,
    lostStolen: null,
    duplicateCount: null,
  },
  DLRENEW: {
    renewalYears: null,
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },
  IDRENEW: {
    renewalYears: null,
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },
  DLDUP: {
    dlLostStolen: null,
    phone: '',
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },
  IDDUP: {
    dlLostStolen: null,
    phone: '',
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },
  RDLOPTIN: {
    renewalYears: null,
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },
  RIDOPTIN: {
    renewalYears: null,
    addOrganDonor: false,
    addVeteransDesignation: false,
    selectedDonations: [],
  },

  EMERCONTACT: {
    contacts: [],
  },
  DLCOA: {
    issueDate: '',
    streetAddress: '',
    poBox: '',
    city: '',
    state: null,
    zipcode: '',
    zip4: '',
    countyCode: '',
    hasDriverLicense: true,
    hasVehicleRegistration: true,
    vehicles: [],
    updateVoterRegistration: 'Y',
  },
}

// Maps selectedDonations workflow keys → API payload field names
const DONATION_FIELD_MAP = {
  DONATEODTF: 'dlDonateOdtf',
  DONATEPBCC: 'dlDonatePbcc',
  DONATEPCRF: 'dlDonatePcrf',
  DONATEVTF: 'dlDonateVtf',
  DONATECTF: 'dlDonateCtfd',
  DONATEKTF: 'dlDonateKtf',
}

const cloneDefaults = (serviceType) => structuredClone(WORKFLOW_DEFAULTS[serviceType] ?? {})

export const useWorkflowInProgressStore = defineStore('workflowInProgress', () => {
  const activeServiceType = ref(null)
  const workflows = reactive({})

  const setActiveServiceType = (serviceType) => {
    activeServiceType.value = serviceType
    if (!workflows[serviceType]) {
      workflows[serviceType] = cloneDefaults(serviceType)
    }
  }

  const clearAll = () => {
    activeServiceType.value = null
    //delete the values inside the workflows object
    for (const key of Object.keys(workflows)) delete workflows[key]
  }

  const current = computed(() => workflows[activeServiceType.value] ?? null)

  const resetWorkflow = (serviceType) => {
    if (WORKFLOW_DEFAULTS[serviceType]) {
      workflows[serviceType] = cloneDefaults(serviceType)
    }
  }

  // uuid of the cart item currently being edited, null when not in edit mode
  const pendingEditUuid = ref(null)

  const REVERSE_DONATION_FIELD_MAP = {
    dlDonateOdtf: 'DONATEODTF',
    dlDonatePbcc: 'DONATEPBCC',
    dlDonatePcrf: 'DONATEPCRF',
    dlDonateVtf: 'DONATEVTF',
    dlDonateCtfd: 'DONATECTF',
    dlDonateKtf: 'DONATEKTF',
  }

  const hydrateRenewal = (serviceType, cartItem, tempTransactionDetails) => {
    workflows[serviceType].renewalYears = tempTransactionDetails.renewalYears ?? null
    workflows[serviceType].addOrganDonor = tempTransactionDetails.addOrganDonor ?? false
    workflows[serviceType].addVeteransDesignation = tempTransactionDetails.addVeteransDesignation ?? false
    // Restore selectedDonations from tempTransactionDetails if present, otherwise
    // reverse-map from the boolean donation flags on the cartItem
    workflows[serviceType].selectedDonations =
      tempTransactionDetails.selectedDonations ??
      Object.entries(REVERSE_DONATION_FIELD_MAP)
        .filter(([apiField]) => cartItem[apiField] === true)
        .map(([, workflowKey]) => workflowKey)
  }
  const hydrateDuplicate = (serviceType, cartItem, tempTransactionDetails) => {
    workflows[serviceType].dlLostStolen = tempTransactionDetails.dlLostStolen ?? cartItem.dlLostStolen ?? null
    workflows[serviceType].phone = tempTransactionDetails.phone ?? ''
    workflows[serviceType].addOrganDonor = tempTransactionDetails.addOrganDonor ?? false
    workflows[serviceType].addVeteransDesignation = tempTransactionDetails.addVeteransDesignation ?? false
    workflows[serviceType].selectedDonations =
      tempTransactionDetails.selectedDonations ??
      Object.entries(REVERSE_DONATION_FIELD_MAP)
        .filter(([apiField]) => cartItem[apiField] === true)
        .map(([, workflowKey]) => workflowKey)
  }

  const hydrateCoa = (serviceType, cartItem, tempTransactionDetails) => {
    workflows[serviceType].streetAddress = cartItem.address1 ?? ''
    workflows[serviceType].poBox = cartItem.poBox ?? ''
    workflows[serviceType].city = cartItem.city ?? ''
    workflows[serviceType].state = cartItem.state ?? null
    workflows[serviceType].zipcode = cartItem.zip ?? ''
    workflows[serviceType].updateVoterRegistration = cartItem.motorVoterCoa === false ? 'N' : 'Y'
    workflows[serviceType].vehicles = tempTransactionDetails.vehicles ?? []
    workflows[serviceType].hasDriverLicense = tempTransactionDetails.hasDriverLicense ?? true
    workflows[serviceType].hasVehicleRegistration = tempTransactionDetails.hasVehicleRegistration ?? true
  }

  const hydrateFromCartItem = (serviceType, cartItem, tempTransactionDetails) => {
    setActiveServiceType(serviceType)

    const HYDRATORS = {
      EMERCONTACT: () => {
        workflows[serviceType].contacts = (cartItem.emergencyContactCartItems ?? []).map((c) => ({
          id: c.seqNo?.toString() ?? crypto.randomUUID(),
          firstName: c.firstName ?? '',
          lastName: c.lastName ?? '',
          middleInitial: c.middleInit ?? '',
          suffix: c.nameSuffix ?? '',
          relationship: c.relationship ?? '',
          addressLine1: c.addressLine1 ?? '',
          addressLine2: c.addressLine2 ?? '',
          city: c.city ?? '',
          state: c.state ?? '',
          zipcode: c.zip ?? '',
          email: c.email ?? '',
          homePhone: c.homePhone ?? '',
          cellPhone: c.cellPhone ?? '',
          workPhone: c.workPhone ?? '',
          name: [c.firstName, c.middleInit?.trim() ? c.middleInit + '.' : '', c.lastName, c.nameSuffix]
            .filter(Boolean)
            .join(' '),
          primaryContact: c.seqNo === 1,
          seqNo: c.seqNo,
        }))
      },
      DLRENEW: () => hydrateRenewal(serviceType, cartItem, tempTransactionDetails),
      IDRENEW: () => hydrateRenewal(serviceType, cartItem, tempTransactionDetails),
      DLDUP: () => hydrateDuplicate(serviceType, cartItem, tempTransactionDetails),
      IDDUP: () => hydrateDuplicate(serviceType, cartItem, tempTransactionDetails),
      RDLOPTIN: () => hydrateDuplicate(serviceType, cartItem, tempTransactionDetails),
      RIDOPTIN: () => hydrateDuplicate(serviceType, cartItem, tempTransactionDetails),
      DLCOA: () => hydrateCoa(serviceType, cartItem, tempTransactionDetails),
      IDCOA: () => hydrateCoa(serviceType, cartItem, tempTransactionDetails),
    }

    HYDRATORS[serviceType]?.()
  }

  // ── Fee Calculation ───────────────────────────────────────────────────────

  const calculateFee = async (serviceCode, item) => {
    const userLogin = useUserLogin()
    const transactionID = userLogin.loginResponse?.transactionOutputDTO?.transactionID ?? null
    const { idNumber: dlNumber } = item
    const workflow = workflows[serviceCode] ?? {}
    const selectedDonations = new Set(workflow.selectedDonations ?? [])

    const donationFlags = Object.fromEntries(
      Object.entries(DONATION_FIELD_MAP).map(([workflowKey, apiField]) => [
        apiField,
        selectedDonations.has(workflowKey),
      ]),
    )

    const serviceFields = {}
    if (workflow.renewalYears) {
      serviceFields.renewalYears = workflow.renewalYears
    }
    if (workflow.dlLostStolen) {
      serviceFields.dlLostStolen = workflow.dlLostStolen
    }

    const payload = {
      transactionID,
      dlNumber,
      serviceType: serviceCode,
      ...donationFlags,
      ...serviceFields,
    }

    try {
      const response = await axios.post(DRIVER_ENDPOINT.FEE_CALC, payload)
      return response.data.data
    } catch (e) {
      LOGGER.logException(e)
      throw new Error(`Failed to calculate fee for service: ${serviceCode}`, e)
    }
  }

  const calculateVehicleFee = async (serviceCode) => {
    const userLogin = useUserLogin()
    const vsLoginResponse = userLogin.vsLoginResponse
    const workflow = workflows[serviceCode] ?? {}
    const selectedDonations = new Set(workflow.selectedDonations ?? [])

    const donationFlags = Object.fromEntries(
      Object.entries(VEHICLE_DONATION_FIELD_MAP).map(([workflowKey, apiField]) => [
        apiField,
        selectedDonations.has(workflowKey),
      ]),
    )

    const payload = {
      transactionID: vsLoginResponse?.transactionOutputDTO?.transactionID ?? null,
      idNumber: vsLoginResponse?.titleNum ?? vsLoginResponse?.legacyTitleNum,
      serviceType: serviceCode,
      renewalYears: String(workflow.renewalYears ?? workflow.renewalLength).padStart(2, '0'),
      dupRegCount: String(workflow.duplicateCount ?? 0).padStart(2, '0'),
      ...donationFlags,
    }

    try {
      const response = await axios.post(VEHICLE_ENDPOINT.FEE_CALC, payload)
      return response?.data?.data ?? null
    } catch (e) {
      LOGGER.logException(e)
      throw new Error(`Failed to calculate fee for service: ${serviceCode}`, e)
    }
  }

  // ── PDPI / PDPR ───────────────────────────────────────────────────────────

  const pdprResult = ref(null)

  const callPdpi = async () => {
    const userLogin = useUserLogin()
    const loginResponse = userLogin.loginResponse
    const idNumber = loginResponse?.olNum
    const transactionID = loginResponse?.transactionOutputDTO?.transactionID ?? null
    const serviceType = activeServiceType.value

    const response = await axios.post(DRIVER_ENDPOINT.PDPI, { idNumber, transactionID, serviceType })
    return response.data
  }

  const callPdpr = async () => {
    const userLogin = useUserLogin()
    const loginResponse = userLogin.loginResponse
    const idNumber = loginResponse?.olNum
    const transactionID = loginResponse?.transactionOutputDTO?.transactionID ?? null
    const serviceType = activeServiceType.value

    const response = await axios.post(DRIVER_ENDPOINT.PDPR, { idNumber, transactionID, serviceType })
    pdprResult.value = response.data.data
    return pdprResult.value
  }

  const clearPdprResult = () => {
    pdprResult.value = null
  }

  // ── COA-specific helpers ──────────────────────────────────────────────────

  const formatPoBox = (value) => {
    if (!value) return ''
    const trimmed = value.trim()
    return /^\d+$/.test(trimmed) ? `PO BOX ${value}` : value.toUpperCase()
  }

  const isAddressValid = computed(() => {
    const coa = workflows['DLCOA']
    return !!(coa?.streetAddress && coa?.city && coa?.state && coa?.zipcode)
  })

  const isIssueDateValid = computed(() => !!workflows['DLCOA']?.issueDate)

  const formattedAddress = computed(() => {
    if (!isAddressValid.value) return null
    const coa = workflows['DLCOA']
    return {
      address1: coa.streetAddress.toUpperCase(),
      poBox: formatPoBox(coa.poBox),
      city: coa.city.toUpperCase(),
      state: coa.state,
      zip: coa.zipcode,
      countyCode: coa.countyCode,
    }
  })

  const formattedAddressDisplay = computed(() => {
    if (!isAddressValid.value) return ''
    const coa = workflows['DLCOA']
    let display = coa.streetAddress.toUpperCase()
    if (coa.poBox) display += `\n${formatPoBox(coa.poBox)}`
    display += `\n${coa.city.toUpperCase()}, ${coa.state} ${coa.zipcode}`
    return display
  })

  const setIssueDate = (date) => {
    if (workflows['DLCOA']) workflows['DLCOA'].issueDate = date
  }

  const setAddress = (addressData) => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.streetAddress = addressData.streetAddress || ''
    coa.poBox = addressData.poBox || ''
    coa.city = addressData.city || ''
    coa.state = addressData.state || null
    coa.zipcode = addressData.zipcode || ''
  }

  const updateNormalizedAddress = (normalizedData) => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.streetAddress = normalizedData.streetAddress || coa.streetAddress
    coa.poBox = normalizedData.poBox || coa.poBox
    coa.city = normalizedData.city || coa.city
    coa.state = normalizedData.state || coa.state
    coa.zipcode = normalizedData.zipcode || coa.zipcode
    coa.zip4 = normalizedData.zip4 || coa.zip4
    coa.countyCode = normalizedData.countyCode || coa.countyCode
  }

  const setServiceFlags = (flags) => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.hasDriverLicense = flags.hasDriverLicense ?? true
    coa.hasVehicleRegistration = flags.hasVehicleRegistration ?? true
  }

  const clearAddress = () => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.streetAddress = ''
    coa.poBox = ''
    coa.city = ''
    coa.state = null
    coa.zipcode = ''
    coa.zip4 = ''
    coa.countyCode = ''
  }

  const addVehicle = (vehicle) => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.vehicles.push(vehicle)
    coa.hasVehicleRegistration = true
  }

  const removeVehicle = (vehicleId) => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.vehicles = coa.vehicles.filter((v) => v.id !== vehicleId)
    coa.hasVehicleRegistration = coa.vehicles.length > 0
  }

  const clearVehicles = () => {
    const coa = workflows['DLCOA']
    if (!coa) return
    coa.vehicles = []
    coa.hasVehicleRegistration = false
  }

  return {
    activeServiceType,
    workflows,
    current,
    pendingEditUuid,
    setActiveServiceType,
    resetWorkflow,
    hydrateFromCartItem,
    calculateFee,
    calculateVehicleFee,

    // PDPI / PDPR
    pdprResult,
    callPdpi,
    callPdpr,
    clearPdprResult,

    // COA
    isAddressValid,
    isIssueDateValid,
    formattedAddress,
    formattedAddressDisplay,
    setIssueDate,
    setAddress,
    updateNormalizedAddress,
    setServiceFlags,
    clearAddress,
    addVehicle,
    removeVehicle,
    clearVehicles,
    clearAll,
  }
})
